import { useState, useRef, useEffect, useCallback } from "react";
import { openMailto } from "@/utils/mailto";

/* ─────────────────────────────────────────────────────────
   Custom Dropdown (internal)
   ───────────────────────────────────────────────────────── */

interface DropdownOption {
  label: string;
  sub?: React.ReactNode;
}

interface CustomDropdownProps {
  label: string;
  placeholder: string;
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
}

function CustomDropdown({ label, placeholder, options, value, onChange }: CustomDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selected = options.find((o) => o.label === value);

  return (
    <div ref={ref} className="relative flex flex-col gap-1">
      <label className="text-[11px] sm:text-[12px] lg:text-[13px] font-medium text-[#090d2c] font-albert-sans">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 lg:py-2.5 rounded-[10px] border bg-white text-left transition-all duration-200 ${
          open ? "border-[#122d63] ring-2 ring-[#122d63]/10" : "border-[#e0e1e6] hover:border-[#c0c1c8]"
        }`}
      >
        <span className={`text-[12px] lg:text-[13px] font-albert-sans truncate ${value ? "text-[#090d2c]" : "text-[#090d2c]/40"}`}>
          {selected ? (
            <span className="flex items-center gap-2">
              {selected.label}
              {selected.sub && <span className="text-[11px]">{selected.sub}</span>}
            </span>
          ) : placeholder}
        </span>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={`shrink-0 text-[#090d2c]/50 transition-transform duration-200 ${open ? "rotate-180" : ""}`}>
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white rounded-[12px] border border-[#e0e1e6] shadow-[0_8px_30px_rgba(0,0,0,0.12)] overflow-hidden">
          <div className="py-1 max-h-[200px] overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => { onChange(option.label); setOpen(false); }}
                className={`w-full text-left px-3.5 py-2 flex flex-col gap-0.5 transition-colors duration-150 ${
                  value === option.label ? "bg-[#122d63]/[0.05]" : "hover:bg-[#f6f6f9]"
                }`}
              >
                <span className="text-[12px] lg:text-[13px] font-semibold text-[#090d2c] font-albert-sans">{option.label}</span>
                {option.sub && <span className="text-[11px] text-[#d4a017]">{option.sub}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Stars
   ───────────────────────────────────────────────────────── */

function Stars({ count }: { count: number }) {
  return (
    <span className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= count ? "text-[#f5b731]" : "text-[#d4d5da]"}>★</span>
      ))}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────
   Compact File Upload
   ───────────────────────────────────────────────────────── */

function FileUploadArea() {
  const [files, setFiles] = useState<File[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (newFiles: FileList | null) => {
    if (newFiles) setFiles((prev) => [...prev, ...Array.from(newFiles)]);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] sm:text-[12px] lg:text-[13px] font-medium text-[#090d2c] font-albert-sans">
        Attachments <span className="text-[#090d2c]/40 font-normal">(Optional)</span>
      </label>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        className="flex items-center gap-3 px-3 py-2 lg:py-2.5 rounded-[10px] border border-dashed border-[#d4d5da] hover:border-[#122d63]/40 bg-[#f9f9fb] cursor-pointer transition-all duration-200"
      >
        <input ref={inputRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" className="hidden" onChange={(e) => handleFiles(e.target.files)} />
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#122d63" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <span className="text-[11px] sm:text-[12px] lg:text-[13px] text-[#090d2c]/60 font-albert-sans">
          Drop files or <span className="text-[#122d63] underline">browse</span> — PDF, JPG, PNG, DOC
        </span>
      </div>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-0.5">
          {files.map((file, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#f6f6f9] border border-[#e0e1e6] text-[11px] text-[#090d2c] font-albert-sans">
              {file.name.length > 20 ? file.name.slice(0, 17) + "..." : file.name}
              <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="hover:text-[#f24d2b] transition-colors">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Dropdown Data
   ───────────────────────────────────────────────────────── */

const feedbackTypes: DropdownOption[] = [
  { label: "Billing" },
  { label: "Payments" },
  { label: "Customer Service" },
  { label: "Technical Support" },
  { label: "Emergency / Maintenance" },
  { label: "New Connection" },
  { label: "Website / Portal / Mobile App" },
  { label: "General Feedback" },
];

const ratingOptions: DropdownOption[] = [
  { label: "Very Dissatisfied", sub: <Stars count={1} /> },
  { label: "Dissatisfied", sub: <Stars count={2} /> },
  { label: "Neutral", sub: <Stars count={3} /> },
  { label: "Satisfied", sub: <Stars count={4} /> },
  { label: "Very Satisfied", sub: <Stars count={5} /> },
];

/* ─────────────────────────────────────────────────────────
   Modal Component — Responsive Horizontal Layout

   Mobile (< 640px):  Full-screen vertical, scrollable
   Tablet (640–1023):  Centered vertical card, scrollable
   Desktop (≥ 1024):   Large horizontal (left info + right form), fits viewport
   ───────────────────────────────────────────────────────── */

interface FeedbackModalProps {
  onClose: () => void;
  presetType?: string;
}

export function FeedbackModal({ onClose, presetType }: FeedbackModalProps) {
  const [visible, setVisible] = useState(false);
  const [feedbackType, setFeedbackType] = useState(presetType || "");
  const [rating, setRating] = useState("");
  const [consent, setConsent] = useState(false);
  const [fullName, setFullName] = useState("");
  const [accountRef, setAccountRef] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [feedbackText, setFeedbackText] = useState("");

  // Enter animation
  useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setVisible(true));
    });
  }, []);

  // Escape key
  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(onClose, 200);
  }, [onClose]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [handleClose]);

  const inputClass = "w-full px-3 py-2 lg:py-2.5 rounded-[10px] border border-[#e0e1e6] bg-white text-[12px] lg:text-[13px] text-[#090d2c] font-albert-sans placeholder:text-[#090d2c]/40 focus:outline-none focus:border-[#122d63] focus:ring-2 focus:ring-[#122d63]/10 transition-all duration-200";

  return (
    <div className="fixed inset-0 z-[1100] font-albert-sans">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"}`}
        onClick={handleClose}
      />

      {/* ── Mobile: full-screen scroll wrapper | Tablet+: centered ── */}
      <div className="absolute inset-0 sm:flex sm:items-center sm:justify-center overflow-y-auto sm:overflow-visible sm:p-6" onClick={handleClose}>
        <div
          className={`relative w-full sm:max-w-[600px] lg:max-w-[1280px] min-h-full sm:min-h-0 bg-white sm:rounded-[20px] lg:rounded-[24px] shadow-[0_25px_50px_rgba(0,0,0,0.25)] overflow-hidden will-change-[transform,opacity] transition-[transform,opacity] duration-200 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 lg:top-4 lg:right-4 z-10 w-8 h-8 lg:w-9 lg:h-9 rounded-full bg-white/90 hover:bg-[#e8e9ed] flex items-center justify-center transition-colors shadow-sm"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#090d2c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>

          {/* ── Layout: vertical on mobile/tablet, horizontal on desktop ── */}
          <div className="flex flex-col lg:flex-row lg:h-[min(90vh,720px)]">

            {/* ── Left Panel — Info ── */}
            <div className="lg:w-[400px] xl:w-[440px] shrink-0 bg-gradient-to-br from-[#122d63] to-[#0a1d42] p-6 sm:p-8 lg:p-10 flex flex-col gap-6 lg:gap-0 lg:justify-between">
              {/* Title block */}
              <div className="flex flex-col gap-3 lg:gap-4">
                <span className="inline-block w-fit px-3 py-1 rounded-full bg-white/[0.12] text-white text-[11px] sm:text-[12px] font-semibold tracking-[-0.02em] uppercase">
                  Share Your Experience
                </span>
                <h2 className="text-[22px] sm:text-[26px] lg:text-[32px] xl:text-[36px] tracking-[-0.04em] font-bold text-white leading-[1.1]">
                  Submit Your Feedback
                </h2>
                <p className="text-[12px] sm:text-[13px] lg:text-[14px] text-white/60 leading-[1.5] max-w-[360px]">
                  Share your experience, report an issue, or suggest improvements. We read every submission.
                </p>
              </div>

              {/* Info items */}
              <div className="flex flex-col gap-3 lg:gap-4">
                {[
                  {
                    title: "Acknowledged in 24h",
                    desc: "Every submission gets a unique reference",
                    icon: (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                      </svg>
                    ),
                  },
                  {
                    title: "Investigated in 3–5 days",
                    desc: "Dedicated team reviews every case",
                    icon: (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    ),
                  },
                  {
                    title: "Follow-up within 7 days",
                    desc: "We contact you with the resolution",
                    icon: (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
                      </svg>
                    ),
                  },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-[8px] bg-white/[0.1] flex items-center justify-center shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-[12px] lg:text-[13px] font-semibold text-white">{item.title}</p>
                      <p className="text-[11px] text-white/50">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Emergency note */}
              <div className="flex items-center gap-2.5 px-3.5 py-3 rounded-[10px] bg-[#f24d2b]/20 border border-[#f24d2b]/30">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f24d2b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <p className="text-[11px] lg:text-[12px] text-white/80 leading-[1.4]">
                  <span className="font-semibold text-[#f24d2b]">Smell gas?</span> Call 800-GASIS immediately. Don't use this form for emergencies.
                </p>
              </div>
            </div>

            {/* ── Right Panel — Form ── */}
            <div className="flex-1 p-6 sm:p-8 lg:p-8 xl:p-10 lg:overflow-y-auto">
              <form onSubmit={(e) => {
                e.preventDefault();
                if (!consent) return;
                const lines = [
                  `Name: ${fullName}`,
                  `Account/Ref: ${accountRef}`,
                  `Email: ${email}`,
                  `Phone: ${phone}`,
                  `Type: ${feedbackType}`,
                  `Rating: ${rating}`,
                  "",
                  feedbackText,
                ];
                openMailto({
                  subject: `Feedback: ${feedbackType || "General"} — ${fullName || "Customer"}`,
                  body: lines.join("\n"),
                });
                handleClose();
              }} className="flex flex-col gap-3 lg:gap-4">
                {/* 2-col grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] sm:text-[12px] lg:text-[13px] font-medium text-[#090d2c] font-albert-sans">Full Name</label>
                    <input type="text" placeholder="Enter your full name" value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] sm:text-[12px] lg:text-[13px] font-medium text-[#090d2c] font-albert-sans">Account / Reference No.</label>
                    <input type="text" placeholder="e.g. GAS-12345678" value={accountRef} onChange={(e) => setAccountRef(e.target.value)} className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] sm:text-[12px] lg:text-[13px] font-medium text-[#090d2c] font-albert-sans">Email Address</label>
                    <input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[11px] sm:text-[12px] lg:text-[13px] font-medium text-[#090d2c] font-albert-sans">Phone Number</label>
                    <input type="tel" placeholder="+971 XX XXX XXXX" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
                  </div>
                  <CustomDropdown label="Type of Feedback" placeholder="Select type" options={feedbackTypes} value={feedbackType} onChange={setFeedbackType} />
                  <CustomDropdown label="Rating" placeholder="Rate your experience" options={ratingOptions} value={rating} onChange={setRating} />
                </div>

                {/* Textarea */}
                <div className="flex flex-col gap-1">
                  <label className="text-[11px] sm:text-[12px] lg:text-[13px] font-medium text-[#090d2c] font-albert-sans">Your Feedback</label>
                  <textarea rows={3} placeholder="Describe your experience, issue, or suggestion..." value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} className={`${inputClass} resize-none`} />
                </div>

                {/* File Upload */}
                <FileUploadArea />

                {/* Consent + Submit */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1 lg:pt-2">
                  <label className="flex items-start gap-2.5 cursor-pointer group/check flex-1 min-w-0">
                    <div className="relative mt-0.5 shrink-0">
                      <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="sr-only" />
                      <div className={`w-[18px] h-[18px] rounded-[5px] border-2 flex items-center justify-center transition-all duration-200 ${consent ? "bg-[#122d63] border-[#122d63]" : "border-[#d4d5da] group-hover/check:border-[#122d63]/40"}`}>
                        {consent && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className="text-[11px] sm:text-[12px] text-[rgba(10,14,46,0.65)] leading-[1.4]">
                      I consent to GASIS processing this data. <a href="/privacy-policy" className="text-[#122d63] underline">Privacy Policy</a>
                    </span>
                  </label>

                  <button type="submit" className="shrink-0 flex items-center justify-center gap-2 py-2.5 lg:py-3 pl-5 lg:pl-6 pr-3.5 lg:pr-4 bg-[#090d2c] hover:bg-[#1a1f45] rounded-[40px] transition-all duration-200 group/btn">
                    <span className="text-[13px] lg:text-[15px] tracking-[-0.02em] font-medium text-white whitespace-nowrap">Submit Feedback</span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="text-white transition-transform duration-300 group-hover/btn:translate-x-1">
                      <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
